<script setup lang="ts">
import {
	computed,
	onMounted,
	ref,
	watch
} from "vue";
import {
	ChatsApi,
	SearchDialog,
	SearchMessage
} from "@/ api/ChatsApi";
import { debounce } from "lodash";
import AppLoader from "@/components/common/AppLoader.vue";
import { highlightSearchText } from "@/utils/textHelpers";
import ChatDialog from "@/components/main/chat-panel/ChatDialog.vue";

const props = defineProps<{
	search: string;
}>()

const emit = defineEmits<{
	(e: 'on-open-dialog', value: SearchDialog, isCreated: boolean): void,
	(e: 'on-open-message', value: SearchMessage): void,
}>()

const loading = ref(true);
const isCreatingChat = ref(false)
const dialogs = ref<SearchDialog[] | null>()
const messages = ref<SearchMessage[] | null>(null)

const isListLoading = computed(() => loading.value || isCreatingChat.value)

const onFetchDialogsAndMessages = async () => {
	if (!props.search) {
		dialogs.value = null
		messages.value = null
		loading.value = false
		return
	}
	try {
		loading.value = true;
		await Promise.all([
			ChatsApi.searchDialogs(props.search),
			ChatsApi.searchMessages(props.search)
		]).then(results => {
			dialogs.value = results[0].data ?? null;
			messages.value = results[1].data ?? null;
		})
	} catch (e) {
		console.error(e)
	} finally {
		loading.value = false;
	}
}

const openDialog = async (dialog: SearchDialog) => {
	if (isCreatingChat.value) {
		return
	}

	if (!dialog.IsPotential) {
		emit('on-open-dialog', dialog, false)
		return
	}

	try {
		isCreatingChat.value = true
		const response = await ChatsApi.createChatWithYouOrOneFriend(dialog.Id)
		emit('on-open-dialog', { ...dialog, Id: response.Id, IsPotential: false }, true)
	} catch (e) {
		console.error(e)
	} finally {
		isCreatingChat.value = false
	}
}

const openMessage = (message: SearchMessage) => {
	emit('on-open-message', message)
}

const debouncedSearch = debounce(onFetchDialogsAndMessages, 500)

watch(() => props.search, () => {
	debouncedSearch()
})

const dialogsStyle = computed(() => {
	if (!dialogs.value || isListLoading.value) {
		return {
			flex: 1
		}
	}

	return {
		height: 'fit-content',
	}
})

const messagesStyle = computed(() => {
	if (!messages.value || isListLoading.value) {
		return {
			flex: 1
		}
	}

	return {
		height: 'fit-content',
	}
})

onMounted(async () => {
	await onFetchDialogsAndMessages()
})
</script>

<template>
	<div class="chats-search-list">
		<div
			class="chats-search-list__dialogs"
			:style="dialogsStyle"
		>
			<div class="chats-search-list__dialogs-title">{{ $t('chat_list_title') }}</div>
			<div class="chats-search-list__dialogs-list">
				<app-loader v-if="isListLoading" />
				<div
					v-else-if="!dialogs?.length"
					class="empty-list"
				>
					{{ $t('nothing_found') }}
				</div>
				<template v-else>
					<div
						v-for="dialog in dialogs"
						class="chats-search-list__dialogs-list-item"
						:key="dialog.Id"
						@click="openDialog(dialog)"
					>
						<div class="chats-search-list__dialogs-list-item-logo">
							<img
								v-if="dialog.PhotoLink"
								v-img-fade="dialog.PhotoLink"
								class="chats-search-list__dialogs-list-item-logo-img"
								alt="Avatar"
							/>
							<div
								v-else
								class="chats-search-list__dialogs-list-item-logo-text"
							>
								{{ dialog.PhotoLiter }}
							</div>
						</div>
						<div
							class="chats-search-list__dialogs-list-item-name"
							v-html="highlightSearchText(props.search, dialog.Name)"
						/>
					</div>
				</template>
			</div>
		</div>
		<div
			class="chats-search-list__messages"
			:style="messagesStyle"
		>
			<div class="chats-search-list__messages-title">{{ $t('messages_label') }}</div>
			<div class="chats-search-list__messages-list">
				<app-loader v-if="isListLoading" />
				<div
					v-else-if="!messages?.length"
					class="empty-list"
				>
					{{ $t('nothing_found') }}
				</div>
				<template v-else>
					<div
						v-for="message in messages"
						class="chats-search-list__messages-list"
						:key="message.Id"
					>
						<chat-dialog
							:dialog="message"
							:from-search="props.search"
							@on-open="openMessage(message)"
						/>
					</div>
				</template>
			</div>
		</div>
	</div>
</template>

<style scoped lang="scss">
.chats-search-list {
	display: flex;
	flex-direction: column;
	flex: 1;
	background: #fff;
	border-bottom-left-radius: 8px;
	border-bottom-right-radius: 8px;
	padding: 0;
	min-height: 0;
	overflow: auto;
	
	&__messages,
	&__dialogs {
		display: flex;
		flex-direction: column;
		position: relative;
		color: #4D4D4D;
		padding: 10px;

		&-title {
			font-size: 14px;
			line-height: 18px;
			font-weight: 500;
		}
	}

	&__dialogs {
		&-list {
			display: flex;
			flex-direction: column;
			gap: 4px;

			&-item {
				display: flex;
				padding: 4px;
				border-radius: 4px;

				&:hover {
					cursor: pointer;
					background-color: rgba(229, 229, 229, 0.3568627451);
				}

				&-logo {
					display: flex;
					align-items: center;
					margin-right: 5px;
					height: 25px;
					width: 25px;

					&-img,
					&-text {
						height: 25px;
						width: 25px;
						border-radius: 50%;
					}

					&-text {
						display: flex;
						align-items: center;
						justify-content: center;
						font-size: 14px;
						text-transform: uppercase;
						color: #fff;
						background-color: #567CA3;
					}
				}

				&-name {
					display: flex;
					align-items: center;
					font-size: 14px;
					line-height: 18px;
					color: #4D4D4D;
					font-weight: 500;
					white-space: pre-wrap;
				}
			}
		}
	}

	.empty-list {
		font-size: 12px;
		line-height: 16px;
	}
}
</style>
